import { QboInvoiceHistory } from "@/components/invoices/qbo-invoice-history";
import { RouteShell } from "@/components/route-shell";
import { downloadCsv } from "@/lib/download-csv";
import { trpc } from "@/lib/trpc";
import { formatDollars } from "@payrollmart-invoice/shared";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Download, Plus, Search, Star } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { BulkActionBar } from "./-bulk-action-bar";
import { useBulkActions } from "./-bulk-confirm";
import { buildPageNumbers } from "./-helpers";
import { Pagination } from "./-pagination";
import { type SortDir, SortHeader, type SortKey } from "./-sort-header";

export const Route = createFileRoute("/_app/invoices/")({
	component: InvoicesPageRoute,
});

function InvoicesPageRoute() {
	return (
		<RouteShell>
			<InvoicesPage />
		</RouteShell>
	);
}

// R18 (2026-04-19): brand-family hexes flipped to `brand-*` semantic tokens
// defined in `styles.css`. Neutral `skipped` variant retained — its hexes
// (`#f3f4f6` / `#6b7280`) are covered by the existing `--color-draft*`
// tokens, but that family migration is a separate pass.
const statusStyles: Record<string, string> = {
	draft: "bg-brand-primary-soft text-brand-primary-on-soft",
	approved: "bg-brand-success-soft text-brand-success-on-soft",
	pushed: "bg-brand-warn-soft text-brand-warn-on-soft",
	paid: "bg-brand-success-soft text-brand-success-on-soft",
	skipped: "bg-[#f3f4f6] text-[#6b7280]",
};

type StatusFilter = "draft" | "approved" | "pushed" | "paid" | "skipped" | undefined;

function InvoicesPage() {
	const [statusFilter, setStatusFilter] = useState<StatusFilter>();
	const [payrollOnly, setPayrollOnly] = useState(true);
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(1);
	const [sortBy, setSortBy] = useState<SortKey>("created");
	const [sortDir, setSortDir] = useState<SortDir>("desc");
	const [selected, setSelected] = useState<Set<string>>(() => new Set());
	const pageSize = 25;

	const { data, isLoading } = trpc.invoices.list.useQuery(
		{
			status: statusFilter,
			payrollCustomer: payrollOnly || undefined,
			search: search || undefined,
			sortBy,
			sortDir,
			page,
			pageSize,
		},
		{ throwOnError: true },
	);
	const { data: qboStatus } = trpc.qbo.status.useQuery();
	const utils = trpc.useUtils();

	const exportCsv = trpc.export.invoicesCsv.useQuery({ status: statusFilter }, { enabled: false });

	const handleExport = async () => {
		const result = await exportCsv.refetch();
		if (result.error) {
			toast.error(result.error.message || "Failed to export CSV");
			return;
		}
		if (!result.data) {
			toast.error("Export returned no data");
			return;
		}
		downloadCsv(result.data.csv, result.data.filename);
		toast.success("CSV downloaded");
	};

	function handleSort(key: SortKey) {
		if (sortBy === key) {
			setSortDir(sortDir === "asc" ? "desc" : "asc");
		} else {
			setSortBy(key);
			setSortDir(key === "customer" ? "asc" : "desc");
		}
		setPage(1);
		setSelected(new Set());
	}

	function handleStatusChange(value: string) {
		setStatusFilter((value || undefined) as StatusFilter);
		setPage(1);
		setSelected(new Set());
	}

	function handleSearchChange(value: string) {
		setSearch(value);
		setPage(1);
		setSelected(new Set());
	}

	function handlePageChange(next: number) {
		setPage(next);
		setSelected(new Set());
	}

	const totalPages = data ? Math.ceil(data.total / pageSize) : 0;
	const pageItems = buildPageNumbers(page, totalPages);

	const items = data?.items ?? [];
	const visibleIds = useMemo(() => items.map((i) => i.id), [items]);
	const selectedRows = useMemo(() => items.filter((i) => selected.has(i.id)), [items, selected]);
	const selectedCount = selectedRows.length;

	// Strict eligibility: a bulk action is enabled only when every selected
	// row meets that action's precondition. Anything mixed disables the
	// button — forces the user to filter first, which keeps the backend
	// all-or-nothing transaction honest.
	const draftCount = selectedRows.filter((r) => r.status === "draft").length;
	const pushableCount = selectedRows.filter(
		(r) => r.status === "approved" && !r.qboInvoiceId,
	).length;
	const canApproveBulk = selectedCount > 0 && draftCount === selectedCount;
	const canSkipBulk = canApproveBulk;
	const canDeleteBulk = canApproveBulk;
	const canPushBulk =
		selectedCount > 0 && Boolean(qboStatus?.connected) && pushableCount === selectedCount;

	const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
	const someVisibleSelected = visibleIds.some((id) => selected.has(id));

	function toggleOne(id: string, checked: boolean) {
		setSelected((prev) => {
			const next = new Set(prev);
			if (checked) next.add(id);
			else next.delete(id);
			return next;
		});
	}

	function toggleAllVisible(checked: boolean) {
		setSelected((prev) => {
			const next = new Set(prev);
			for (const id of visibleIds) {
				if (checked) next.add(id);
				else next.delete(id);
			}
			return next;
		});
	}

	function clearSelection() {
		setSelected(new Set());
	}

	async function refresh() {
		await utils.invoices.list.invalidate();
		await utils.invoices.dashboardSummary.invalidate();
	}

	const bulk = useBulkActions({ selectedRows, clearSelection, refresh });

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-xl font-semibold text-[#1e1e20]">
						Invoices{data ? ` (${data.total})` : ""}
					</h1>
					<p className="mt-1 text-sm text-[#6b7280]">Review and manage generated invoices.</p>
				</div>
				<div className="flex items-center gap-2">
					<button
						type="button"
						onClick={handleExport}
						disabled={!data?.items.length || exportCsv.isFetching}
						className="inline-flex items-center gap-1.5 rounded-md border border-[#e5e7eb] bg-white px-3 py-1.5 text-sm font-medium text-[#1e1e20] transition-colors hover:bg-[#f8f9fb] disabled:opacity-50"
					>
						<Download className="h-3.5 w-3.5" strokeWidth={1.5} />
						{exportCsv.isFetching ? "Exporting..." : "Export CSV"}
					</button>
					<Link
						to="/invoices/generate"
						className="inline-flex items-center gap-1.5 rounded-md bg-brand-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-primary-hover"
					>
						<Plus className="h-3.5 w-3.5" strokeWidth={2} />
						Generate Invoice
					</Link>
				</div>
			</div>

			{/* Filters */}
			<div className="flex flex-wrap items-center gap-3">
				<div className="relative">
					<Search
						className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#9ca3af]"
						strokeWidth={1.5}
					/>
					<input
						type="text"
						placeholder="Search customers..."
						value={search}
						onChange={(e) => handleSearchChange(e.target.value)}
						className="h-9 w-56 rounded border border-[#d1d5db] bg-white pl-8 pr-3 text-sm text-[#1e1e20] placeholder:text-[#9ca3af] focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
					/>
				</div>
				<select
					value={statusFilter ?? ""}
					onChange={(e) => handleStatusChange(e.target.value)}
					className="h-9 rounded border border-[#d1d5db] bg-white px-3 text-sm text-[#1e1e20] focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
				>
					<option value="">All Statuses</option>
					<option value="draft">Draft</option>
					<option value="approved">Approved</option>
					<option value="pushed">Pushed to QBO</option>
					<option value="paid">Paid</option>
					<option value="skipped">Skipped</option>
				</select>
				<button
					type="button"
					onClick={() => {
						setPayrollOnly(!payrollOnly);
						setPage(1);
						setSelected(new Set());
					}}
					className={`inline-flex h-9 items-center gap-1.5 rounded border px-3 text-sm font-medium transition-colors ${
						payrollOnly
							? "border-[#f59e0b] bg-brand-warn-soft text-brand-warn-on-soft"
							: "border-[#d1d5db] bg-white text-[#6b7280] hover:bg-[#f8f9fb]"
					}`}
				>
					<Star
						className="h-3.5 w-3.5"
						strokeWidth={1.5}
						fill={payrollOnly ? "currentColor" : "none"}
					/>
					{payrollOnly ? "Payroll Customers" : "All Customers"}
				</button>
			</div>

			{/* Bulk action bar */}
			<BulkActionBar
				selectedCount={selectedCount}
				draftCount={draftCount}
				pushableCount={pushableCount}
				canApproveBulk={canApproveBulk}
				canSkipBulk={canSkipBulk}
				canDeleteBulk={canDeleteBulk}
				canPushBulk={canPushBulk}
				bulkPending={bulk.bulkPending}
				qboConnected={Boolean(qboStatus?.connected)}
				deletePending={bulk.deletePending}
				skipPending={bulk.skipPending}
				approvePending={bulk.approvePending}
				pushPending={bulk.pushPending}
				onClear={clearSelection}
				onDelete={bulk.handleDelete}
				onSkip={bulk.handleSkip}
				onApprove={bulk.handleApprove}
				onPush={bulk.handlePush}
			/>

			{/* Invoice Table */}
			<div className="rounded-lg border border-[#e5e7eb] bg-white">
				{isLoading ? (
					<div className="divide-y divide-[#f3f4f6]">
						{[1, 2, 3].map((i) => (
							<div key={i} className="h-11 animate-pulse bg-[#f8f9fb]" />
						))}
					</div>
				) : !data?.items.length ? (
					<div className="py-12 text-center">
						<p className="text-sm font-semibold text-[#1e1e20]">No invoices found</p>
						<p className="mt-1 text-xs text-[#6b7280]">
							{search || statusFilter
								? "Try adjusting your filters."
								: "Generate invoices from approved document extractions."}
						</p>
					</div>
				) : (
					<>
						<table className="w-full text-sm">
							<thead>
								<tr className="border-b-2 border-[#e5e7eb] bg-[#f8f9fb] text-left">
									<th className="w-10 px-4 py-2.5">
										<input
											type="checkbox"
											aria-label="Select all on this page"
											checked={allVisibleSelected}
											ref={(el) => {
												if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected;
											}}
											onChange={(e) => toggleAllVisible(e.target.checked)}
											className="h-4 w-4 cursor-pointer rounded border-[#d1d5db] text-brand-primary focus:ring-brand-primary/20"
										/>
									</th>
									<SortHeader
										label="Customer"
										sortKey="customer"
										currentSort={sortBy}
										currentDir={sortDir}
										onSort={handleSort}
									/>
									<SortHeader
										label="Period"
										sortKey="period"
										currentSort={sortBy}
										currentDir={sortDir}
										onSort={handleSort}
									/>
									<SortHeader
										label="Status"
										sortKey="status"
										currentSort={sortBy}
										currentDir={sortDir}
										onSort={handleSort}
									/>
									<SortHeader
										label="Source Charges"
										sortKey="sourceCharges"
										currentSort={sortBy}
										currentDir={sortDir}
										onSort={handleSort}
										align="right"
									/>
									<SortHeader
										label="Invoiced"
										sortKey="total"
										currentSort={sortBy}
										currentDir={sortDir}
										onSort={handleSort}
										align="right"
									/>
									<SortHeader
										label="Created"
										sortKey="created"
										currentSort={sortBy}
										currentDir={sortDir}
										onSort={handleSort}
									/>
								</tr>
							</thead>
							<tbody>
								{data.items.map((inv) => {
									const checked = selected.has(inv.id);
									return (
										<tr
											key={inv.id}
											className={`border-b border-[#f3f4f6] transition-colors duration-150 last:border-0 hover:bg-[#f8f9fb] ${
												checked ? "bg-[#f5f3ff]" : ""
											}`}
										>
											<td className="px-4 py-3">
												<input
													type="checkbox"
													aria-label={`Select invoice for ${inv.customerName ?? "Unknown"}`}
													checked={checked}
													onChange={(e) => toggleOne(inv.id, e.target.checked)}
													className="h-4 w-4 cursor-pointer rounded border-[#d1d5db] text-brand-primary focus:ring-brand-primary/20"
												/>
											</td>
											<td className="px-4 py-3">
												<Link
													to="/invoices/$invoiceId"
													params={{ invoiceId: inv.id }}
													className="font-medium text-brand-primary hover:underline"
												>
													{inv.customerName ?? "Unknown"}
												</Link>
											</td>
											<td className="px-4 py-3 text-[#6b7280]">
												{inv.periodStart} to {inv.periodEnd}
											</td>
											<td className="px-4 py-3">
												<span
													className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusStyles[inv.status] ?? ""}`}
												>
													{inv.status}
												</span>
											</td>
											<td className="px-4 py-3 text-right tabular-nums text-[#6b7280]">
												{formatDollars(inv.sourceChargesTotal)}
											</td>
											<td className="px-4 py-3 text-right tabular-nums font-medium text-[#1e1e20]">
												{formatDollars(inv.totalAmount)}
											</td>
											<td className="px-4 py-3 text-[#6b7280]">
												{inv.createdAt ? new Date(inv.createdAt).toLocaleDateString() : "--"}
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>

						{/* Pagination */}
						<Pagination
							page={page}
							totalPages={totalPages}
							pageSize={pageSize}
							total={data.total}
							pageItems={pageItems}
							onPageChange={handlePageChange}
						/>
					</>
				)}
			</div>

			{/* QBO Invoice History */}
			<QboInvoiceHistory />

			{bulk.dialog}
		</div>
	);
}
